import classNames from 'classnames';
import { Smartphone } from 'lucide-react';
import { bool, func, number, object, string } from 'prop-types';
import React, { useEffect, useState } from 'react';
import logoImage from '../../../../assets/logo.png';
import nowLogo from '../../../../assets/images/now-logo.png';
import {
  Avatar,
  BrandingIconCard,
  Button,
  InlineTextButton,
  LinkedLogo,
  Menu,
  MenuContent,
  MenuItem,
  MenuLabel,
  Modal,
  NamedLink,
  ResponsiveImage,
} from '../../../../components';
import { ACCOUNT_SETTINGS_PAGES } from '../../../../routing/routeConfiguration';
import { useRouteConfiguration } from '../../../../context/routeConfigurationContext';
import { FormattedMessage, intlShape } from '../../../../util/reactIntl';
import {
  BUSINESS_PROFILE_LISTING_TYPE,
  DEAL_LISTING_TYPE,
  SERVICE_LISTING_TYPE,
  propTypes,
} from '../../../../util/types';
import TopbarSearchForm from '../TopbarSearchForm/TopbarSearchForm';
import CustomLinksMenu from './CustomLinksMenu/CustomLinksMenu';
import css from './TopbarDesktop.module.css';
import { getCartCount } from '../../../CartPage/CartPage.duck';
import { useSelector } from 'react-redux';
import OneSignal from 'react-onesignal';
import IconCollection from '../../../../components/IconCollection/IconCollection';
import { Icon } from '../../../../components/IconAdd/IconAdd.example';
import { useLocation } from 'react-router-dom';
import { trackEventsMixpanel } from '../../../../mixpanel/mixpanel';
import { Events, mixPanelEventsData, mixPanelEventsScreens } from '../../../../mixpanel';
import { onGetNotificationsV2 } from '../../../../util/api';
import AuthIntentLink from '../../../../components/AuthIntentLink/AuthIntentLink';
import { getMarketplaceEntities } from '../../../../ducks/marketplaceData.duck';
import {
  getSafeOnboardingListings,
  getOnboardingNavigationPath,
  getNextOnboardingStep,
  ONBOARDING_STEP,
} from '../../../../util/onboardingHelpers';
import GetAppModal from '../GetAppModal/GetAppModal';
import LanguageToggle from '../LanguageToggle/LanguageToggle';

const lucideProps = {
  fill: 'none',
  'aria-hidden': true,
};

const draftId = '00000000-0000-0000-0000-000000000000';
const getLocalizedCmsPath = (pathname, language) => {
  const isArabic = language === 'Arabic';

  if (pathname === '/p/about' || pathname === '/p/about-arabic') {
    return isArabic ? '/p/about-arabic' : '/p/about';
  }

  if (pathname === '/privacy-policy' || pathname === '/privacy-policy-arabic') {
    return isArabic ? '/privacy-policy-arabic' : '/privacy-policy';
  }

  return pathname;
};

/**
 * Component for the "Signup as Customer" link
 */
const SignupLinkCustomer = () => (
  <AuthIntentLink
    routeName="SignupForUserTypePage"
    params={{ userType: 'customer' }}
    className={css.topbarLink}
  >
    <span className={css.topbarLinkLabel}>
      <FormattedMessage id="TopbarDesktop.signupCustomer" />
    </span>
  </AuthIntentLink>
);

/**
 * Component for the "Signup as Merchant" link
 */
const SignupLinkMerchant = () => (
  <AuthIntentLink
    routeName="SignupForUserTypePage"
    params={{ userType: 'provider' }}
    className={css.topbarLink}
  >
    <span className={css.topbarLinkLabel}>
      <FormattedMessage id="TopbarDesktop.signupMerchant" />
    </span>
  </AuthIntentLink>
);

/**
 * Component for the "Login" link
 */
const LoginLink = () => (
  <AuthIntentLink routeName="LoginPage" className={css.signinButton}>
    <span className={css.signupLabel}>
      <FormattedMessage id="TopbarDesktop.login1" />
    </span>
  </AuthIntentLink>
);

const LoginLinkMerchant = () => (
  <AuthIntentLink
    routeName="LoginForUserTypePage"
    className={css.signinButton}
    params={{ userType: 'provider' }}
  >
    <span className={css.signupLabel}>
      <FormattedMessage id="TopbarDesktop.login1" />
    </span>
  </AuthIntentLink>
);

/**
 * Component for the "Inbox" link with notification dot
 */
const InboxLink = ({ notificationCount, currentUserHasListings }) => {
  const notificationDot = notificationCount > 0 ? <div className={css.notificationDot} /> : null;

  return (
    <NamedLink
      className={css.topbarLink}
      name="InboxPage"
      params={{ tab: currentUserHasListings ? 'sales' : 'orders' }}
    >
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.inbox" />
        {notificationDot}
      </span>
    </NamedLink>
  );
};

/**
 * Component for the profile menu with options for settings and logout
 */
const ProfileMenu = ({ currentPage, currentUser, onLogout }) => {
  /**
   * Determines the class name for the active menu item based on the current page
   */
  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    return currentPage === page || isAccountSettingsPage ? css.currentPage : null;
  };

  const { userType, profileListingId } = currentUser?.attributes?.profile?.publicData || {};
  const currentUserId = currentUser?.id?.uuid;
  const isProvider = userType === 'provider';
  const shouldRedirectToMerchantPage = currentUserId && isProvider && profileListingId;

  const location = useLocation();
  return (
    <Menu>
      <MenuLabel className={css.profileMenuLabel} isOpenClassName={css.profileMenuIsOpen}>
        <Avatar className={css.avatar} user={currentUser} disableProfileLink />
      </MenuLabel>
      <MenuContent className={css.profileMenuContent}>
        {/* <MenuItem key="ManageListingsPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('ManageListingsPage'))}
            name="ManageListingsPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.yourListingsLink" />
          </NamedLink>
        </MenuItem> */}
        <MenuItem key={shouldRedirectToMerchantPage ? 'ViewBusinessPage' : 'ProfilePage'}>
          <NamedLink
            className={classNames(
              css.menuLink,
              currentPageClass(shouldRedirectToMerchantPage ? 'ViewBusinessPage' : 'ProfilePage')
            )}
            name={
              shouldRedirectToMerchantPage
                ? 'ViewBusinessPage'
                : currentUserId
                ? 'ProfilePage'
                : 'ProfileSettingsPage'
            }
            params={
              shouldRedirectToMerchantPage
                ? { listingType: 'business-profile', id: profileListingId }
                : currentUserId
                ? { id: currentUserId }
                : {}
            }
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.profileSettingsLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="AccountSettingsPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('AccountSettingsPage'))}
            name="AccountSettingsPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.accountSettingsLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="InboxBookingPage">
          <NamedLink
            className={classNames(
              css.menuLink,
              (location?.pathname.includes('/sales/bookings') ||
                location?.pathname.includes('/orders/bookings')) &&
                css.activeLink
            )}
            name="InboxNewPage"
            params={{ tab: `${isProvider ? 'sales' : 'orders'}`, state: 'bookings' }}
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.bookings" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="InboxOrderPage">
          <NamedLink
            className={classNames(
              css.menuLink,
              (location?.pathname.includes('/sales/orders') ||
                location?.pathname.includes('/orders/orders')) &&
                css.activeLink
            )}
            name="InboxNewPage"
            params={{ tab: `${isProvider ? 'sales' : 'orders'}`, state: 'orders' }}
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.orders" />
          </NamedLink>
        </MenuItem>

        {/* <MenuItem key="CartPage">
          <NamedLink
            className={classNames(
              css.menuLink,
              currentPageClass('CartPage'),
              isProvider && css.hidden
            )}
            name="CartPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.yourCartLink" />
          </NamedLink>
        </MenuItem> */}

        <MenuItem key="logout">
          <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.logout" />
          </InlineTextButton>
        </MenuItem>
      </MenuContent>
    </Menu>
  );
};

const OfflineCustomerProfileMenu = ({ currentPage, currentUser, onLogout }) => {
  /**
   * Determines the class name for the active menu item based on the current page
   */
  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    return currentPage === page || isAccountSettingsPage ? css.currentPage : null;
  };

  const { userType } = currentUser?.attributes?.profile?.publicData || {};
  const isProvider = userType === 'provider';

  return (
    <Menu>
      <MenuLabel className={css.profileMenuLabel} isOpenClassName={css.profileMenuIsOpen}>
        <Avatar className={css.avatar} user={currentUser} disableProfileLink />
      </MenuLabel>

      <MenuContent className={css.profileMenuContent}>
        <MenuItem key="SignupForUserTypePage">
          <AuthIntentLink
            routeName="SignupForUserTypePage"
            params={{ userType: 'customer' }}
            className={classNames(css.menuLink, currentPageClass('SignupForUserTypePage'))}
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.signup" />
          </AuthIntentLink>
        </MenuItem>

        <MenuItem key="LoginForUserTypePage">
          <AuthIntentLink
            routeName="LoginForUserTypePage"
            params={{ userType: 'customer' }}
            className={classNames(css.menuLink, currentPageClass('LoginForUserTypePage'))}
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.login" />
          </AuthIntentLink>
        </MenuItem>
      </MenuContent>
    </Menu>
  );
};

/**
 * The main topbar component for desktop layout
 */

const CartLink = ({ notificationCount }) => {
  const notificationDot = notificationCount > 0 ? <div className={css.notificationDot} /> : null;
  return (
    <NamedLink className={css.topbarLink} name="CartPage">
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.cart" />
        {notificationDot}
      </span>
    </NamedLink>
  );
};

const TopbarDesktop = ({
  className,
  config,
  customLinks,
  currentUser,
  currentPage,
  rootClassName,
  currentUserHasListings,
  notificationCount,
  intl,
  isAuthenticated,
  onLogout,
  onSearchSubmit,
  initialSearchFormValues,
  merchantMaybe,
}) => {
  const [mounted, setMounted] = useState(false);
  const [isOnboardingModalOpen, setIsOnboardingModalOpen] = useState(true);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  const state = useSelector(state => state);
  const routeConfiguration = useRouteConfiguration();
  const { publicData } = currentUser?.attributes?.profile || {};

  const arabicLanguage =
    typeof window !== 'undefined' && localStorage.getItem('language') === 'Arabic';
  const {
    userType,
    wishlist_deal_ids = [],
    wishlist_product_ids = [],
    wishlist_service_ids = [],
    profileListingId,
  } = publicData || {};

  const currentUserId = currentUser?.id?.uuid;
  const isProvider = userType === 'provider';
  const isCustomer = userType === 'customer';
  const notificationUserType = isProvider ? 'merchant' : isCustomer ? 'customer' : null;
  const shouldRedirectToMerchantPage = currentUserId && isProvider && profileListingId;
  const getOwnListing = listingId => {
    const listings = getMarketplaceEntities(state, [{ id: listingId, type: 'listing' }]) || [];
    return listings[0];
  };
  const onboardingListings = getSafeOnboardingListings(currentUserId, getOwnListing);
  const existingOwnListings = Object.values(
    state.ManageListingsPage?.ownEntities?.ownListing || {}
  );
  const getPreferredOnboardingListing = listingType => {
    const persistedListing = onboardingListings.find(
      listing => listing.type === listingType && listing.id && listing.id !== draftId
    );
    if (persistedListing) {
      return persistedListing;
    }

    const fallbackStoredListing = onboardingListings.find(listing => listing.type === listingType);
    const existingOwnListing = existingOwnListings.find(
      listing => listing?.attributes?.publicData?.listingType === listingType
    );

    if (existingOwnListing?.id?.uuid) {
      return {
        id: existingOwnListing.id.uuid,
        type: existingOwnListing.attributes?.publicData?.listingType,
      };
    }

    return fallbackStoredListing || null;
  };

  // The first thing this provider still owes (agreement, service, deal or
  // business profile), or null when onboarding is fully done. The agreement
  // counts: a merchant created from the CRM has everything published and only
  // needs to sign.
  const nextOnboardingStep = getNextOnboardingStep(currentUser);
  const isOnboardingComplete = nextOnboardingStep === null;
  const continueOnboardingTarget =
    // A merchant who skipped the deal step has nothing to create there, so
    // getNextOnboardingStep never returns DEAL for them (no loop with no exit).
    nextOnboardingStep === ONBOARDING_STEP.DEAL
      ? getPreferredOnboardingListing(DEAL_LISTING_TYPE) || {
          id: draftId,
          type: DEAL_LISTING_TYPE,
        }
      : nextOnboardingStep === ONBOARDING_STEP.BUSINESS
      ? getPreferredOnboardingListing(BUSINESS_PROFILE_LISTING_TYPE) || {
          id: draftId,
          type: BUSINESS_PROFILE_LISTING_TYPE,
        }
      : // SERVICE, or AGREEMENT -- for the latter the target is irrelevant:
        // getOnboardingNavigationPath sends an unsigned merchant to the
        // agreement step whatever the target.
        getPreferredOnboardingListing(SERVICE_LISTING_TYPE) || {
          id: draftId,
          type: SERVICE_LISTING_TYPE,
        };
  // A brand-new service/product target (no draft started yet) routes through
  // the category picker first instead of landing straight on AddItemForm —
  // see getOnboardingNavigationPath.
  const continueOnboardingPath = getOnboardingNavigationPath(
    { id: continueOnboardingTarget.id, formType: continueOnboardingTarget.type },
    routeConfiguration,
    currentUser
  );

  const location = useLocation();
  const isOnCartPage = location.pathname === '/cart';
  // Set by getOnboardingNavigationPath on the category-picker hand-off
  // (NewListingCategoryPage, reached via continueOnboardingPath itself when
  // onboarding isn't finished yet). That page renders this same Topbar, so
  // without this the "complete your profile" nudge would immediately cover
  // the very step it just sent the merchant to.
  const isOnboardingCategoryHandoff =
    new URLSearchParams(location?.search || '').get('onboarding') === '1';

  // useEffect(() => {
  //   // Ensure this code runs only on the client side
  //   if (
  //     typeof window !== 'undefined' &&
  //     currentUser?.attributes?.profile?.publicData?.userType === 'provider'
  //   ) {
  //     OneSignal.init({
  //       appId: '0840a204-f095-4160-804d-5e818fbc7665',
  //       // You can add other initialization options here
  //       notifyButton: {
  //         enable: true,
  //       },
  //       allowLocalhostAsSecureOrigin: true,
  //       // Uncomment the below line to run on localhost. See: https://documentation.onesignal.com/docs/local-testing
  //       // allowLocalhostAsSecureOrigin: true
  //     });
  //     if (currentUser?.id?.uuid) {
  //       OneSignal.login(currentUser?.id?.uuid);
  //     } else {
  //       OneSignal.logout();
  //     }
  //   }
  // }, [currentUser?.id?.uuid]);

  useEffect(() => {
    setMounted(true);
  }, []);

  const marketplaceName = config.marketplaceName;
  const authenticatedOnClientSide = mounted && isAuthenticated;
  const isAuthenticatedOrJustHydrated = isAuthenticated || !mounted;

  // Determine if the search form should take up space
  const giveSpaceForSearch = customLinks == null || customLinks?.length === 0;
  const classes = classNames(
    rootClassName || css.root,
    className,
    arabicLanguage && css.arabicDirection
  );

  const isProviderAuthenticated = isAuthenticated && userType === 'provider';

  // Conditional rendering based on authentication status
  const inboxLinkMaybe = authenticatedOnClientSide ? (
    <InboxLink
      notificationCount={notificationCount}
      currentUserHasListings={currentUserHasListings}
    />
  ) : null;

  const profileMenuMaybe = authenticatedOnClientSide ? (
    <ProfileMenu currentPage={currentPage} currentUser={currentUser} onLogout={onLogout} />
  ) : null;

  const offlineProfileMenuMaybe = !authenticatedOnClientSide ? (
    <OfflineCustomerProfileMenu
      currentPage={currentPage}
      currentUser={currentUser}
      onLogout={onLogout}
    />
  ) : null;

  const signupLinkCustomerMaybe = isAuthenticatedOrJustHydrated ? null : <SignupLinkCustomer />;
  const signupLinkMerchantMaybe = isAuthenticatedOrJustHydrated ? null : <SignupLinkMerchant />;
  const loginLinkMaybe = isAuthenticatedOrJustHydrated ? null : <LoginLink />;
  const LoginLinkMerchantMaybe = isAuthenticatedOrJustHydrated ? null : <LoginLinkMerchant />;

  const cartCount = getCartCount(state, isOnCartPage) || 0;

  // Get Wishlist count
  const wishlistCount =
    wishlist_deal_ids?.length + wishlist_product_ids?.length + wishlist_service_ids?.length;

  const cartLinkMaybe = authenticatedOnClientSide ? (
    <CartLink notificationCount={cartCount} />
  ) : null;

  const [selectedLanguage, setSelectedLanguage] = useState();
  const [getAppOpen, setGetAppOpen] = useState(false);

  useEffect(() => {
    if (!mounted || !authenticatedOnClientSide || !currentUserId || !notificationUserType) {
      setUnreadNotificationCount(0);
      return;
    }

    let cancelled = false;

    onGetNotificationsV2({
      userId: currentUserId,
      userType: notificationUserType,
      page: 1,
      perPage: 1,
      markAsRead: false,
    })
      .then(response => {
        if (cancelled) {
          return;
        }

        const unreadFromApi = Number(response?.unread_count);
        if (Number.isFinite(unreadFromApi)) {
          setUnreadNotificationCount(Math.max(0, unreadFromApi));
          return;
        }

        const unreadFromPage = (response?.notifications || []).filter(item => item && !item.isRead)
          .length;
        setUnreadNotificationCount(unreadFromPage);
      })
      .catch(() => {
        if (!cancelled) {
          setUnreadNotificationCount(0);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [mounted, authenticatedOnClientSide, currentUserId, notificationUserType, location?.pathname]);

  useEffect(() => {
    setMounted(true);
    const storedLanguage = typeof window !== 'undefined' && localStorage.getItem('language');
    if (typeof window !== 'undefined' && !localStorage.getItem('rendered')) {
      localStorage.setItem('language', 'English');
      localStorage.setItem('rendered', 'English');
      setLanguage('English');
    }
    if (storedLanguage) {
      setSelectedLanguage(storedLanguage);
    }
  }, []);

  const setLanguage = lg => {
    typeof window != 'undefined' && [
      localStorage.setItem('language', lg),
      localStorage.setItem('rendered', lg),
    ];
    if (typeof window !== 'undefined') {
      const { pathname, search, hash } = window.location;
      const localizedPath = getLocalizedCmsPath(pathname, lg);
      const nextUrl = `${localizedPath}${search}${hash}`;
      const currentUrl = `${pathname}${search}${hash}`;

      if (nextUrl !== currentUrl) {
        window.location.assign(nextUrl);
      } else {
        window.location.reload();
      }
    }
  };
  const handleLanguageChange = (lg, isCustomer) => {
    setLanguage(lg);
    setSelectedLanguage(lg);

    if (lg && isCustomer) {
      trackEventsMixpanel(Events.CUSTOMER_SIGNUP_COMPLETE, {
        action_type: mixPanelEventsData.CUSTOMER_SETUP_LANGUAGE,
        screen: mixPanelEventsScreens.NAV_BAR,
        user_type: 'customer',
        user_id: currentUserId,
        selected_language: lg?.toLowerCase(),
      });
    }
  };

  const languageToggle = (
    <LanguageToggle
      className={css.customerLanguage}
      selectedLanguage={selectedLanguage}
      onToggle={lg => handleLanguageChange(lg, isCustomer)}
    />
  );

  const showVendorLogo = userType === 'provider' || merchantMaybe;
  const logoRouteName = showVendorLogo ? 'LandingPageMerchant' : 'LandingPage';
  const logoIconName = showVendorLogo ? 'vendor_logo' : 'customer_logo';

  const customerTopbar = (
    <div className={css.customerHeaderOuter}>
      <div className={css.customerHeader}>
        <div className={css.customerWrapper}>
          <div className={classNames(css.customerLogo, showVendorLogo && css.customerLogoVendor)}>
            <NamedLink className={css.customerLogoLink} name={logoRouteName}>
              <img src={nowLogo} alt="NOW Offers" className={css.customerLogoImage} />
            </NamedLink>
          </div>

          <nav className={css.customerNav}>
            <NamedLink className={css.link} name="LandingPage">
              <FormattedMessage id="TopbarDesktop.home" />
            </NamedLink>
            <NamedLink
              className={css.link}
              name="AlgoliaSearchPage"
              params={{ listingType: 'services' }}
            >
              <FormattedMessage id="TopbarDesktop.services" />
            </NamedLink>
            <NamedLink
              className={css.link}
              name="AlgoliaSearchPage"
              params={{ listingType: 'deals' }}
            >
              <FormattedMessage id="TopbarDesktop.deals" />
            </NamedLink>
            {!isAuthenticated ? (
              <NamedLink className={css.link} name="LandingPageMerchant">
                <FormattedMessage id="TopbarDesktop.listYourBusiness" />
              </NamedLink>
            ) : null}
          </nav>

          <div className={css.customerRightBox}>
            {isAuthenticated ? (
              <>
                <div className={css.userProfile}>{profileMenuMaybe}</div>
                {languageToggle}
                <NamedLink className={css.link} name="NotificationPage">
                  <div className={css.notificationIcon}>
                    <span>
                      <BrandingIconCard type="notification" />
                    </span>
                    {unreadNotificationCount > 0 ? (
                      <span className={css.notificationCountBadge}>
                        {unreadNotificationCount > 99 ? '99+' : unreadNotificationCount}
                      </span>
                    ) : null}
                  </div>
                </NamedLink>
                <NamedLink className={css.wishlistPage} name="WishlistPage">
                  <IconCollection name="wishlist_icon" />
                  <span className={css.cartCount}>{wishlistCount || 0}</span>
                </NamedLink>
              </>
            ) : (
              <>
                <button
                  type="button"
                  className={css.getAppButton}
                  onClick={() => setGetAppOpen(true)}
                  aria-label={intl.formatMessage({ id: 'TopbarDesktop.getAppAriaLabel' })}
                >
                  <span className={css.getAppIconWrap} aria-hidden="true">
                    <Smartphone
                      className={css.getAppIconSvg}
                      size={16}
                      strokeWidth={2}
                      {...lucideProps}
                    />
                  </span>
                  <span className={css.getAppLabel}>
                    <FormattedMessage id="TopbarDesktop.getTheApp" />
                  </span>
                </button>
                {languageToggle}
                {loginLinkMaybe}
              </>
            )}
          </div>
        </div>
        <GetAppModal
          isOpen={getAppOpen}
          onClose={() => setGetAppOpen(false)}
          arabicLanguage={arabicLanguage}
        />
      </div>
    </div>
  );

  return (
    <nav className={classes}>
      <div>
        {isProviderAuthenticated ? (
          <div className={css.afterLoginWrapper}>
            <div className={css.loginWrapper}>
              <div className={css.headerLeft}>
                <div className={css.logoWrapper}>
                  <NamedLink className={css.link} name="LandingPage">
                    <IconCollection name="vendor_logo" />
                  </NamedLink>
                </div>
                <div className={css.menuList}>
                  <div className={css.menuNames}>
                    <NamedLink className={css.link} name="LandingPage">
                      <span className={css.menuItemBorder} />
                      <FormattedMessage id="TopbarDesktop.home" />
                    </NamedLink>
                    <NamedLink className={css.link} name="CalenderPage">
                      <span className={css.menuItemBorder} />
                      <FormattedMessage id="TopbarDesktop.calendar" />
                    </NamedLink>

                    <NamedLink
                      className={css.link}
                      name="ViewListingsPage"
                      params={{ listingType: 'deal' }}
                    >
                      <span className={css.menuItemBorder} />
                      <FormattedMessage id="TopbarDesktop.deals" />
                    </NamedLink>
                    <NamedLink
                      className={css.link}
                      name="InboxNewPage"
                      params={{ tab: 'sales', state: 'orders' }}
                    >
                      <span className={css.menuItemBorder} />
                      <FormattedMessage id="TopbarDesktop.orders" />
                    </NamedLink>
                    <NamedLink
                      className={css.link}
                      name="ViewListingsPage"
                      params={{ listingType: 'service' }}
                    >
                      <span className={css.menuItemBorder} />
                      <FormattedMessage id="TopbarDesktop.services" />
                    </NamedLink>
                    {/* Products menu item - Hidden as not currently supported
                        <NamedLink
                          className={css.link}
                          name="ViewListingsPage"
                          params={{ listingType: 'product' }}
                        >
                          <span className={css.menuItemBorder} />
                          <FormattedMessage id="TopbarDesktop.products" />
                        </NamedLink>
                        */}
                    <NamedLink className={css.link} name="MoreActionPage">
                      <span className={css.menuItemBorder} />
                      <FormattedMessage id="TopbarDesktop.more" />
                    </NamedLink>
                  </div>
                </div>
              </div>
              <div className={css.headerRight}>
                {languageToggle}
                <NamedLink
                  className={css.link}
                  name={
                    shouldRedirectToMerchantPage
                      ? 'ViewBusinessPage'
                      : currentUserId
                      ? 'ProfilePage'
                      : 'ProfileSettingsPage'
                  }
                  params={
                    shouldRedirectToMerchantPage
                      ? { listingType: 'business-profile', id: profileListingId }
                      : currentUserId
                      ? { id: currentUserId }
                      : {}
                  }
                >
                  <span>
                    <BrandingIconCard type="setting" />
                  </span>
                </NamedLink>

                <NamedLink className={css.link} name="NotificationPage">
                  <div className={css.notificationIcon}>
                    <span>
                      <BrandingIconCard type="notification" />
                    </span>
                    {unreadNotificationCount > 0 ? (
                      <span className={css.notificationCountBadge}>
                        {unreadNotificationCount > 99 ? '99+' : unreadNotificationCount}
                      </span>
                    ) : null}
                  </div>
                </NamedLink>
                <div className={css.profileIcon}>{profileMenuMaybe}</div>
              </div>
            </div>
          </div>
        ) : (
          customerTopbar
        )}
      </div>

      {/* Complete Onboarding Modal for Providers */}
      {userType === 'provider' && !isOnboardingComplete && !isOnboardingCategoryHandoff ? (
        <Modal
          className={css.completeOnboardingModal}
          id="CompleteOnboardingModal"
          isOpen={isOnboardingModalOpen}
          onClose={() => {
            setIsOnboardingModalOpen(false);
          }}
          usePortal
          contentClassName={css.modalContent}
          onManageDisableScrolling={() => {}} // Add proper scroll management if needed
        >
          <div>
            <div className={css.completeOnboardingText}>
              <FormattedMessage
                id={
                  nextOnboardingStep === ONBOARDING_STEP.AGREEMENT
                    ? 'TopbarDesktop.signAgreement'
                    : 'TopbarDesktop.completeOnboarding'
                }
              />
            </div>
            <a href={continueOnboardingPath} className={css.topbarLink}>
              <Button type="button">
                <FormattedMessage id="TopbarDesktop.continueButtonText" />
              </Button>
            </a>
          </div>
        </Modal>
      ) : null}
    </nav>
  );
};

// Default prop values
TopbarDesktop.defaultProps = {
  rootClassName: null,
  className: null,
  currentUser: null,
  currentPage: null,
  notificationCount: 0,
  initialSearchFormValues: {},
  config: null,
  merchantMaybe: false,
};

// Prop type validation
TopbarDesktop.propTypes = {
  rootClassName: string,
  className: string,
  currentUserHasListings: bool.isRequired,
  currentUser: propTypes.currentUser,
  currentPage: string,
  isAuthenticated: bool.isRequired,
  onLogout: func.isRequired,
  notificationCount: number,
  onSearchSubmit: func.isRequired,
  initialSearchFormValues: object,
  intl: intlShape.isRequired,
  config: object,
  merchantMaybe: bool,
};

export default TopbarDesktop;
